class StickyFilter extends HTMLElement {
  constructor() {
    super();
    this.sections = [];
    this.originalTop = 0;
    this.isSticky = false;
    this.placeholder = null;
    this.scrollTimeout = null;
  }

  connectedCallback() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      // (update 2026-09) no artificial 100 ms delay - the script is deferred, so the DOM is already parsed here
      this.init();
    }
  }

  init() {
    this.generateFilterButtons();
    this.setupStickyBehavior();
    this.makeSticky(); // Make it sticky immediately
    this.bindEvents();
  }

  generateFilterButtons() {
    const sectionHeaders = document.querySelectorAll('.section-header h2');
    const container = this.querySelector('.sticky-filter__container');
    
    if (!container) return;

    sectionHeaders.forEach((header, index) => {
      const text = header.textContent.trim();
      if (text) {
        // Create simple ID from section text (e.g., "bestsellers", "skiskates", etc.)
        // Non-Latin headings (ru, ar, ja, ko, th, zh-TW) would produce an empty id -> fall back to a position-based id
        const headerId = text.toLowerCase().replace(/[^a-z0-9]/g, '') || `sticky-section-${index}`;
        
        // Add ID directly to the H2 heading
        header.setAttribute('id', headerId);
        this.sections.push({ id: headerId, element: header, text: text, index: index });

        // Create button with icon
        const button = document.createElement('button');
        button.className = 'sticky-filter__button';
        button.setAttribute('data-target', headerId);
        
        // Add icon and text - use position-based detection
        const icon = this.getIconForSectionByPosition(index, text, headerId);
        button.innerHTML = icon;
        const label = document.createElement('span');
        label.textContent = text;
        button.appendChild(label);
        
        // Add click event listener
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollToSection(e);
        });
        
        container.appendChild(button);
      }
    });

    // Set first button as active and start section observation
    this.updateActiveSection();
    this.startScrollDetection();
  }

  getIconForSectionByPosition(index, sectionText, sectionId) {
    // Pure position-based icon detection (language-independent)
    // Fixed order: Bestsellers, Value Picks, Skiskates, Skiblades, Nordic, Accessories, More

    // Define icons based on position only - no text detection
    const icons = [
      // 0: Bestsellers
      {
        iconUrl: this.getAttribute('data-bestsellers-icon'),
        alt: 'Bestsellers'
      },
      // 1: Value Picks (Best Value Picks)
      {
        iconUrl: this.getAttribute('data-value-picks-icon'),
        alt: 'Value Picks'
      },
      // 2: Skiskates
      {
        iconUrl: this.getAttribute('data-skiskates-icon'),
        alt: 'Skiskates'
      },
      // 3: Skiblades
      {
        iconUrl: this.getAttribute('data-skiblades-icon'),
        alt: 'Skiblades'
      },
      // 4: Nordic (using original inline SVG)
      {
        iconUrl: null, // Use inline SVG
        alt: 'Nordic',
        inlineSvg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22c0-2.18-.76-4.16-2-5.74V6h-7z"/></svg>'
      },
      // 5: Accessories
      {
        iconUrl: this.getAttribute('data-accessories-icon'),
        alt: 'Accessories'
      },
      // 6+: More (default for any additional sections)
      {
        iconUrl: this.getAttribute('data-more-icon'),
        alt: 'More'
      }
    ];
    
    // Get icon config based on position, default to "More" for positions beyond our defined icons
    const iconConfig = icons[index] || icons[6];
    
    // Return appropriate icon (image or inline SVG)
    if (iconConfig.inlineSvg) {
      return iconConfig.inlineSvg;
    } else if (iconConfig.iconUrl) {
      return `<img src="${iconConfig.iconUrl}" width="16" height="16" alt="${iconConfig.alt}" style="flex-shrink: 0;">`;
    }
    
    // Fallback SVG icon if asset not found
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
  }

  setupStickyBehavior() {
    // Get original position
    this.originalTop = this.getBoundingClientRect().top + window.scrollY;
    
    // Create placeholder element
    this.placeholder = document.createElement('div');
    this.placeholder.style.height = this.offsetHeight + 'px';
    this.placeholder.style.display = 'none';
    this.parentNode.insertBefore(this.placeholder, this.nextSibling);
  }

  scrollToSection(event) {
    // Handle clicks on button or its children (icon/span)
    const button = event.target.closest('.sticky-filter__button');
    if (!button) return;
    
    const targetId = button.getAttribute('data-target');
    const targetHeading = document.getElementById(targetId);
    
    if (targetHeading) {
      // Special case: if it's "Shop Bestsellers", scroll to top of page
      const buttonText = button.textContent.toLowerCase();
      if (buttonText.includes('bestseller') || buttonText.includes('shop')) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        // Normal behavior for other sections
        const headerHeight = this.getHeaderHeight();
        const scrollBuffer = 100; // Larger buffer - scroll further up for better positioning
        const offsetTop = targetHeading.getBoundingClientRect().top + window.scrollY - headerHeight - scrollBuffer;
        
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }

      // Immediately update active state
      this.updateActiveSection(targetId);
    }
  }

  startScrollDetection() {
    // Initial detection
    this.detectActiveSection();
  }

  detectActiveSection() {
    if (this.sections.length === 0) return;
    
    // Larger buffer on desktop for earlier detection
    const isDesktop = window.innerWidth >= 750;
    const baseBuffer = isDesktop ? 180 : 120; // Bigger buffer on desktop
    const triggerPoint = this.getHeaderHeight() + baseBuffer;
    let activeSection = this.sections[0]; // Default to first section
    
    // Check each heading to see if it has passed our trigger point
    for (const section of this.sections) {
      const heading = section.element;
      const headingTop = heading.getBoundingClientRect().top + window.scrollY;
      const currentScrollTop = window.scrollY + triggerPoint;
      
      if (currentScrollTop >= headingTop) {
        activeSection = section;
      }
    }
    
    this.updateActiveSection(activeSection.id);
  }

  updateActiveSection(activeSectionId = null) {
    // If no specific section provided, find the first one in viewport
    if (!activeSectionId && this.sections.length > 0) {
      activeSectionId = this.sections[0].id;
    }
    
    let activeButton = null;
    this.querySelectorAll('.sticky-filter__button').forEach(btn => {
      const isActive = btn.getAttribute('data-target') === activeSectionId;
      btn.classList.toggle('active', isActive);
      if (isActive) {
        activeButton = btn;
      }
    });

    // Auto-scroll to active button on mobile
    if (activeButton) {
      this.scrollToActiveButton(activeButton);
    }
  }

  scrollToActiveButton(activeButton) {
    const container = this.querySelector('.sticky-filter__container');
    if (!container || !activeButton) return;

    // Calculate positions
    const containerRect = container.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();
    
    // Check if button is visible in container
    const buttonLeft = buttonRect.left - containerRect.left + container.scrollLeft;
    const containerWidth = containerRect.width;
    
    // Calculate ideal scroll position (center the button)
    const idealScrollLeft = buttonLeft - (containerWidth / 2) + (buttonRect.width / 2);
    
    // Smooth scroll to position
    container.scrollTo({
      left: idealScrollLeft,
      behavior: 'smooth'
    });
  }

  updateActiveButton(activeButton) {
    this.querySelectorAll('.sticky-filter__button').forEach(btn => {
      btn.classList.remove('active');
    });
    activeButton.classList.add('active');
  }

  getHeaderHeight() {
    const rootStyles = window.getComputedStyle(document.documentElement);
    const cssOffset = parseFloat(rootStyles.getPropertyValue('--sticky-area-height'));

    if (!Number.isNaN(cssOffset)) {
      return cssOffset;
    }

    // Fallback legacy detection if CSS variables are unavailable
    const countdownBar = document.querySelector('.countdown-bar:not(.countdown-bar--hidden)');
    const countdownHeight = countdownBar?.offsetHeight || 0;

    const announcementBar = document.querySelector('.announcement-bar');
    const announcementHeight = announcementBar?.offsetHeight || 0;

    const headerSelectors = [
      '.header',
      'header',
      '[data-header]',
      '.site-header',
      '.shopify-section-header',
      '.shopify-section--header',
      'sticky-header',
      '.sticky-header'
    ];

    let headerHeight = 0;
    for (const selector of headerSelectors) {
      const element = document.querySelector(selector);
      if (element && element.offsetHeight > 0) {
        headerHeight = element.offsetHeight;
        break;
      }
    }

    const total = countdownHeight + announcementHeight + (headerHeight || 80);
    return total;
  }

  applyStickyOffset() {
    // (update 2026-09) Stick to the real bottom edge of the sticky header. Summing the bar heights left a ~1 px gap,
    // because the header intentionally overlaps the countdown bar by 1 px. The filter sits 1 px under the header
    // (z-index 5 vs 90), so no gap can appear because of sub-pixel rounding.
    const header = document.querySelector('.shopify-section--header');
    const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
    const top = headerBottom > 0 ? headerBottom - 1 : this.getHeaderHeight();
    this.style.top = Math.max(0, top) + 'px';
  }

  makeSticky() {
    if (!this.isSticky) {
      this.style.position = 'fixed';
      this.style.left = '0';
      this.style.right = '0';
      // Stacking: z-index 5 keeps filter below header (90), countdown bar (100), drawers (200), and popovers (210)
      this.style.zIndex = '5';
      this.placeholder.style.display = 'block';
      this.placeholder.style.height = this.offsetHeight + 'px';
      this.isSticky = true;
    }

    this.applyStickyOffset();
  }

  handleScroll() {
    this.makeSticky();
    // Update active section based on heading positions
    this.detectActiveSection();
  }

  bindEvents() {
    // Throttled scroll handler for better performance
    window.addEventListener('scroll', () => {
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      this.scrollTimeout = setTimeout(() => {
        this.handleScroll();
      }, 50); // Update every 50ms
    });
    
    window.addEventListener('resize', () => {
      this.originalTop = this.getBoundingClientRect().top + window.scrollY;
      this.placeholder.style.height = this.offsetHeight + 'px';
      this.applyStickyOffset();
    });

    window.addEventListener('countdown-bar:toggle', () => {
      this.applyStickyOffset();
    });
  }
}

// Register the custom element
customElements.define('sticky-filter', StickyFilter);
